import styled, { css } from 'styled-components';
import { Label } from '../../../components/uielements';
import { key, palette } from 'styled-theme';

const tabletBreakpoint = key('breakpoints.tablet', '820px');

const gap = {
  medium: key('sizes.gap.normal', '16px'),
  big: key('sizes.gap.medium', '24px'),
};

const accent = palette('color', 0, '#6366f1');

export const Main = styled.main`
  display: flex;
  height: 100vh;
  width: 100vw;
  position: relative;
  overflow: hidden;

  /* Tablet and mobile share one treatment: panel on top, map below,
     both always visible — not the desktop side-by-side split. */
  @media (max-width: ${tabletBreakpoint}) {
    flex-direction: column;
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
  }
`;

export const SectionLeft = styled.section`
  background-color: ${palette('background', 0)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
  padding: ${(props) => (props.isCollapsed ? '20px 10px' : '120px')};
  width: ${(props) => (props.isCollapsed ? '80px' : '50vw')};
  min-width: ${(props) => (props.isCollapsed ? '80px' : '400px')};
  max-width: ${(props) => (props.isCollapsed ? '80px' : '50vw')};
  position: relative;
  transition: all 0.4s ease-in-out;
  overflow: hidden;
  z-index: 10;
  box-shadow: ${(props) =>
    props.isCollapsed ? '2px 0 8px rgba(0, 0, 0, 0.1)' : '4px 0 20px rgba(0, 0, 0, 0.15)'};

  /* Tablet + mobile: full-width block sized to its content, on top of the
     map — the isCollapsed side-panel-width logic doesn't apply here since
     there's no side panel anymore. Top padding is pushed out to clear the
     fixed navbar overlaying the page (same navbar-clearance issue solved
     in application.style.js's Main margin-top) — without it the heading
     renders directly under/behind the logo. */
  @media (max-width: ${tabletBreakpoint}) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: auto;
    flex: 0 0 auto;
    gap: ${gap.medium};
    padding: 96px ${gap.medium} ${gap.big};
    box-shadow: none;
    justify-content: flex-start;
  }
`;

export const SectionRight = styled.section`
  background-size: cover;
  background-position: center;
  flex: 1;
  position: relative;
  transition: all 0.4s ease-in-out;
  width: ${(props) => (props.isCollapsed ? 'calc(100vw - 80px)' : '50vw')};

  @media (max-width: ${tabletBreakpoint}) {
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    overflow: hidden;
  }
`;

export const SliderToggle = styled.button`
  position: absolute;
  top: 50%;
  right: ${(props) => (props.isCollapsed ? '15px' : '20px')};
  transform: translateY(-50%);
  width: 50px;
  height: 80px;
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 123, 255, 0.3);
  transition: all 0.4s ease-in-out;
  z-index: 1001;

  &:hover {
    background: linear-gradient(90deg, #7b5cff, #60a5fa);
    box-shadow: 0 4px 15px rgba(0, 123, 255, 0.5);
    transform: translateY(-50%) scale(1.05);
  }

  &:focus {
    outline: 3px solid ${accent};
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    transition: transform 0.2s ease-in-out;
  }

  /* Tablet + mobile: panel and map are both always visible — no collapse
     toggle needed, so it's removed rather than left with nothing to do. */
  @media (max-width: ${tabletBreakpoint}) {
    display: none;
  }
`;

export const PanelContent = styled.div`
  opacity: ${(props) => (props.isCollapsed ? '0' : '1')};
  transform: translateX(${(props) => (props.isCollapsed ? '-30px' : '0')});
  transition: all 0.4s ease-in-out;
  transition-delay: ${(props) => (props.isCollapsed ? '0s' : '0.2s')};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
  pointer-events: ${(props) => (props.isCollapsed ? 'none' : 'auto')};
  visibility: ${(props) => (props.isCollapsed ? 'hidden' : 'visible')};

  /* Tablet + mobile: always shown, ignore the collapse state entirely. */
  @media (max-width: ${tabletBreakpoint}) {
    height: auto;
    gap: ${gap.medium};
    opacity: 1;
    transform: none;
    pointer-events: auto;
    visibility: visible;
  }
`;

export const Heading = styled(Label).attrs({
  size: 'header2',
  color: 'black',
  weight: 'bold',
})`
  text-transform: uppercase;
`;

export const SubHeading = styled(Label).attrs({
  size: 'normal',
  color: 'black',
})``;

export const InfoButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-family: 'JetBrains Mono Regular';
  max-width: fit-content;

  & span {
    display: block;
    text-align: left;
    white-space: normal;
    word-break: break-word;
  }
`;

export const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
  }
`;

export const ModalBody = styled.div`
  padding: 16px;
  max-height: 80vh;
  overflow-y: auto;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    color: red;
  }

  &:focus {
    outline: 2px solid #005fcc;
    outline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid #005fcc;
    outline-offset: 2px;
  }
`;

export const ImageContainer = styled.div`
  margin: 16px 0;
  text-align: center;
`;

export const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:focus {
    outline: 2px solid #005fcc;
    outline-offset: 4px;
  }
`;

export const ContentWrapper = styled.div`
  p {
    margin: 12px 0;
    line-height: 1.5;
    color: #333;
  }

  .text-link {
    background: none;
    border: none;
    color: #005fcc;
    padding: 0;
    font: inherit;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover {
      color: #003d80;
    }

    &:focus {
      outline: 2px solid #005fcc;
      outline-offset: 2px;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid #005fcc;
      outline-offset: 2px;
    }
  }
`;

export const ButtonWapper = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;

  @media (max-width: ${tabletBreakpoint}) {
    & > button {
      flex: 1 1 0;
      min-width: 0;
      width: auto;
      max-width: 100% !important;
      padding-left: 8px;
      padding-right: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      white-space: nowrap;
      overflow: hidden;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      svg {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
      }
    }
  }
`;

export const CaptureButton = styled.button`
  width: 100%;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #fde68a;
  }

  & span {
    font-size: 16px;
    font-weight: 400;
    color: #000;
    display: block;
    text-align: center;
  }

  ${({ disabled }) =>
    disabled
      ? css`
          color: black;
          cursor: not-allowed !important;
          background: ${palette('gray', 0)} !important;
          border: none !important;
        `
      : css``}
`;
